import React, { useState, useEffect } from 'react';
import { db, getCollectionName } from './firebase';
import {
  collection, getDocs,
  addDoc, doc, updateDoc, deleteDoc, writeBatch,
} from './firestore-compat';
import { AVAILABLE_MODULES, DEFAULT_PROFILES } from './config/modules';

// ── Paleta ────────────────────────────────────────────────────────────────────
const P = {
  blue:   '#6366f1', green:  '#10b981', orange: '#f59e0b',
  red:    '#ef4444', muted:  '#64748b', border: '#e2e8f0',
  bg:     '#f8fafc', text:   '#1e293b', light:  '#f1f5f9',
};

// ── Estilos ───────────────────────────────────────────────────────────────────
const inp = { padding: '9px 13px', borderRadius: 8, border: `1px solid ${P.border}`, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none', color: P.text };
const btnPri  = { background: P.blue, color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' };
const btnDang = { background: 'none', border: `1px solid ${P.red}`, color: P.red, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const btnSec  = { background: 'none', border: `1px solid ${P.border}`, color: P.muted, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const lbl     = { display: 'block', fontSize: '0.75rem', fontWeight: 700, color: P.muted, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };

const GROUPS = [...new Set(AVAILABLE_MODULES.map(m => m.group))];

const ICON_OPTIONS = ['👤','🛡️','🔍','🏥','🏠','📋','⭐','📅','🤝','📆','👥','💻','📊','🎯','🔑','⚙️','🧩','📌','🌟','💼'];

// ── Modal de Criação / Edição de Perfil ───────────────────────────────────────
function PerfilModal({ perfil, users, onSave, onDelete, onClose }) {
  const isNew = !perfil?.id;

  const [nome,          setNome]          = useState(perfil?.name         || '');
  const [icon,          setIcon]          = useState(perfil?.icon         || '👤');
  const [color,         setColor]         = useState(perfil?.color        || P.blue);
  const [desc,          setDesc]          = useState(perfil?.desc         || '');
  const [reqSetor,      setReqSetor]      = useState(perfil?.requiresSetor  || false);
  const [reqEquipe,     setReqEquipe]     = useState(perfil?.requiresEquipe || false);
  const [perms,         setPerms]         = useState(() => {
    const base = AVAILABLE_MODULES.reduce((a, m) => ({ ...a, [m.id]: { view: false, edit: false } }), {});
    return { ...base, ...(perfil?.permissions || {}) };
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const usersComEssePerfil = users.filter(u => u.perfilId === perfil?.id || u.perfilName === perfil?.name);

  const togglePerm = (modId, tipo) => {
    setPerms(prev => {
      const cur = prev[modId] || { view: false, edit: false };
      const next = { ...cur, [tipo]: !cur[tipo] };
      if (tipo === 'edit' && next.edit) next.view = true;
      if (tipo === 'view' && !next.view) next.edit = false;
      return { ...prev, [modId]: next };
    });
  };

  const authorizeAll = () => {
    setPerms(AVAILABLE_MODULES.reduce((a, m) => ({ ...a, [m.id]: { view: true, edit: true } }), {}));
  };

  const clearAll = () => {
    setPerms(AVAILABLE_MODULES.reduce((a, m) => ({ ...a, [m.id]: { view: false, edit: false } }), {}));
  };

  const handleSave = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    await onSave({ name: nome.trim(), icon, color, desc: desc.trim(), requiresSetor: reqSetor, requiresEquipe: reqEquipe, permissions: perms });
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '20px 26px 16px', borderBottom: `1px solid ${P.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <h3 style={{ margin: 0, color: P.text, fontSize: '1.1rem' }}>{isNew ? '➕ Novo Perfil' : `✏️ Editar: ${perfil.name}`}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: P.muted }}>✕</button>
        </div>

        <div style={{ padding: '22px 26px', flex: 1 }}>
          {/* Identidade do perfil */}
          <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 12, marginBottom: 20 }}>
            {/* Ícone */}
            <div>
              <label style={lbl}>Ícone</label>
              <select style={{ ...inp, fontSize: '1.4rem', textAlign: 'center', padding: '6px' }} value={icon} onChange={e => setIcon(e.target.value)}>
                {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            {/* Nome */}
            <div>
              <label style={lbl}>Nome do perfil *</label>
              <input style={inp} type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Auditora de Qualidade" autoFocus />
            </div>
            {/* Cor */}
            <div>
              <label style={lbl}>Cor de identificação</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 42, height: 38, borderRadius: 6, border: `1px solid ${P.border}`, cursor: 'pointer', padding: 2 }} />
                <div style={{ flex: 1, height: 38, borderRadius: 6, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>{icon}</div>
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Descrição</label>
            <input style={inp} type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ex: Responsável pelas auditorias de qualidade no Prontobaby" />
          </div>

          {/* Requisitos */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 22 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem', color: P.text, userSelect: 'none' }}>
              <input type="checkbox" checked={reqSetor} onChange={e => setReqSetor(e.target.checked)} />
              <span><strong>Requer Setor</strong> — exige setor no cadastro</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem', color: P.text, userSelect: 'none' }}>
              <input type="checkbox" checked={reqEquipe} onChange={e => setReqEquipe(e.target.checked)} />
              <span><strong>Requer Equipe</strong> — exige cooperativa/equipe</span>
            </label>
          </div>

          {/* Matriz de permissões */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: P.text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Permissões de Acesso</h4>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={{ ...btnSec, fontSize: '0.78rem', padding: '5px 12px' }} onClick={clearAll}>✕ Limpar tudo</button>
              <button type="button" style={{ ...btnPri, fontSize: '0.78rem', padding: '5px 12px' }} onClick={authorizeAll}>✅ Autorizar tudo</button>
            </div>
          </div>

          <div style={{ border: `1px solid ${P.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
            {GROUPS.map((group, gi) => {
              const mods = AVAILABLE_MODULES.filter(m => m.group === group);
              return (
                <div key={group}>
                  <div style={{ background: `${P.blue}10`, padding: '6px 14px', fontWeight: 700, fontSize: '0.75rem', color: P.blue, textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: gi > 0 ? `1px solid ${P.border}` : 'none' }}>
                    {group}
                  </div>
                  {mods.map((mod, idx) => {
                    const p = perms[mod.id] || { view: false, edit: false };
                    return (
                      <div key={mod.id} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px', padding: mod.subFunc ? '4px 14px 4px 28px' : '6px 14px', alignItems: 'center', background: mod.subFunc ? '#f5f3ff' : (idx % 2 === 0 ? '#fff' : P.bg), borderTop: `1px solid ${P.light}` }}>
                        <span style={{ fontSize: mod.subFunc ? '0.81rem' : '0.86rem', color: mod.subFunc ? '#7c3aed' : P.text, fontStyle: mod.subFunc ? 'italic' : 'normal' }}>{mod.label}</span>
                        <div style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={p.view} onChange={() => togglePerm(mod.id, 'view')} title="Ver" />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={p.edit} onChange={() => togglePerm(mod.id, 'edit')} title="Editar" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px', padding: '6px 14px', background: P.light }}>
              <span style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 700, textTransform: 'uppercase' }}>Módulo</span>
              <span style={{ textAlign: 'center', fontSize: '0.72rem', color: P.muted, fontWeight: 700, textTransform: 'uppercase' }}>Ver</span>
              <span style={{ textAlign: 'center', fontSize: '0.72rem', color: P.muted, fontWeight: 700, textTransform: 'uppercase' }}>Editar</span>
            </div>
          </div>

          {/* Usuários com este perfil */}
          {!isNew && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', fontWeight: 700, color: P.text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Usuários com este perfil ({usersComEssePerfil.length})
              </h4>
              {usersComEssePerfil.length === 0
                ? <p style={{ color: P.muted, fontSize: '0.85rem', margin: 0 }}>Nenhum usuário com este perfil ainda.</p>
                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {usersComEssePerfil.map(u => (
                      <span key={u.id} style={{ background: `${color}15`, border: `1px solid ${color}40`, color: color, padding: '3px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>
                        {u.name}
                      </span>
                    ))}
                  </div>
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 26px', borderTop: `1px solid ${P.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, background: '#fff' }}>
          {!isNew
            ? confirmDel
              ? <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: P.red, fontWeight: 600 }}>Confirma exclusão?</span>
                  <button style={btnDang} onClick={() => onDelete(perfil.id)}>Excluir</button>
                  <button style={btnSec} onClick={() => setConfirmDel(false)}>Cancelar</button>
                </div>
              : <button style={btnDang} onClick={() => setConfirmDel(true)}>🗑️ Excluir Perfil</button>
            : <div />
          }
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnSec} onClick={onClose}>Cancelar</button>
            <button style={{ ...btnPri, opacity: saving ? 0.7 : 1 }} disabled={!nome.trim() || saving} onClick={handleSave}>
              {saving ? '⏳ Salvando...' : isNew ? '✅ Criar Perfil' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function PerfilConfig({ users = [] }) {
  const [perfis,    setPerfis]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(null); // null | 'new' | { ...perfil }
  const [seeding,   setSeeding]   = useState(false);

  const loadPerfis = () => {
    setLoading(true);
    getDocs(collection(db, getCollectionName('perfis_config')))
      .then(snap => setPerfis(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      .catch(err => console.warn('Erro ao carregar perfis:', err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPerfis(); }, []); // eslint-disable-line

  const handleSave = async (data) => {
    if (modal?.id) {
      await updateDoc(doc(db, getCollectionName('perfis_config'), modal.id), data);
    } else {
      await addDoc(collection(db, getCollectionName('perfis_config')), { ...data, createdAt: new Date() });
    }
    setModal(null);
    loadPerfis();
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, getCollectionName('perfis_config'), id));
    setModal(null);
    loadPerfis();
  };

  const seedDefaultProfiles = async () => {
    setSeeding(true);
    const toId = (p) => (p.roles && p.roles[0]) || p.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const batch = writeBatch(db);
    DEFAULT_PROFILES.forEach(p => {
      batch.set(doc(db, getCollectionName('perfis_config'), toId(p)), { ...p, createdAt: new Date() });
    });
    await batch.commit();
    setSeeding(false);
    loadPerfis();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: P.muted }}>Carregando perfis...</div>;

  return (
    <div style={{ padding: '24px' }}>
      {/* Modal */}
      {modal && (
        <PerfilModal
          perfil={modal === 'new' ? null : modal}
          users={users}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}

      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: '0 0 4px', color: P.text }}>Perfis de Acesso</h3>
          <p style={{ margin: 0, color: P.muted, fontSize: '0.85rem' }}>
            Cada perfil define o que um conjunto de usuários pode ver e editar no sistema.
            Ao criar um usuário, você escolhe o perfil e as permissões são aplicadas automaticamente.
          </p>
        </div>
        <button style={btnPri} onClick={() => setModal('new')}>➕ Novo Perfil</button>
      </div>

      {/* Seed banner */}
      {perfis.length === 0 && (
        <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '20px 24px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Nenhum perfil cadastrado</div>
            <div style={{ color: '#a16207', fontSize: '0.85rem' }}>Crie perfis do zero ou restaure os 12 perfis padrão do sistema.</div>
          </div>
          <button
            style={{ ...btnPri, background: '#f59e0b', whiteSpace: 'nowrap', opacity: seeding ? 0.7 : 1 }}
            onClick={seedDefaultProfiles}
            disabled={seeding}
          >
            {seeding ? '⏳ Criando...' : '✨ Criar perfis padrão'}
          </button>
        </div>
      )}

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
        {perfis.map(p => {
          const nUsers = users.filter(u => u.perfilId === p.id || u.perfilName === p.name).length;
          const modCount = Object.values(p.permissions || {}).filter(v => v.view).length;
          return (
            <button key={p.id} onClick={() => setModal(p)} style={{
              background: '#fff', border: `2px solid ${P.border}`, borderRadius: 14,
              padding: '18px 20px', cursor: 'pointer', textAlign: 'left',
              transition: 'all 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.boxShadow = `0 4px 16px ${p.color}25`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
            >
              {/* Topo */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: `${p.color}18`, border: `2px solid ${p.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>{p.icon}</div>
                <span style={{ background: `${p.color}15`, color: p.color, fontWeight: 700, fontSize: '0.72rem', padding: '3px 10px', borderRadius: 20 }}>{nUsers} usuário{nUsers !== 1 ? 's' : ''}</span>
              </div>
              {/* Info */}
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: P.text, marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: '0.8rem', color: P.muted, lineHeight: 1.4, marginBottom: 10 }}>{p.desc}</div>
              {/* Badges */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ background: P.light, color: P.muted, fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20 }}>{modCount} módulo{modCount !== 1 ? 's' : ''}</span>
                {p.requiresSetor  && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20 }}>Setor</span>}
                {p.requiresEquipe && <span style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20 }}>Equipe</span>}
              </div>
            </button>
          );
        })}
      </div>

      {perfis.length > 0 && (
        <div style={{ marginTop: 24, padding: '14px 18px', background: P.bg, borderRadius: 10, border: `1px solid ${P.border}` }}>
          <p style={{ margin: 0, fontSize: '0.82rem', color: P.muted }}>
            💡 <strong>Como funciona:</strong> Ao criar um usuário pelo wizard, você escolhe um perfil. As permissões marcadas aqui são copiadas para o usuário.
            Após a criação, você pode ajustar as permissões individuais editando o usuário.
          </p>
        </div>
      )}
    </div>
  );
}
